import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackgroundAccents from './BackgroundAccents';
import '../styles/fonts.css';

const About = () => {
  const navigate = useNavigate();

  const pageVariants = {
    initial: {
      y: "-100vh",
      opacity: 0
    },
    animate: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    exit: {
      y: "-100vh",
      opacity: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-vintage-beige font-sans selection:bg-black selection:text-white relative"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <BackgroundAccents primaryColor="bg-emerald-100" secondaryColor="bg-yellow-50" opacity="opacity-30" />

      {/* Top-left HOME button - Vertical text */}
      <motion.button
        onClick={() => navigate('/')}
        className="fixed top-0 left-0 z-50 flex flex-col items-center group hidden md:flex"
        style={{ padding: '20px' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <motion.span
          className="text-black text-sm font-medium"
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)'
          }}
          whileHover={{ letterSpacing: "0.2em" }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          HOME
        </motion.span>
        <motion.div
          className="w-[1px] bg-black"
          initial={{ height: '80px', opacity: 0.4 }}
          whileHover={{ height: '100px', opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          style={{ marginTop: '20px' }}
        />
      </motion.button>

      {/* 2025 Vertical Text */}
      <div className="fixed left-8 bottom-8 z-50 hidden md:block">
        <span
          className="text-black/40 text-[10px] font-sans tracking-widest block"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        >
          © 2025
        </span>
      </div>

      {/* Main Content Area */}
      <main className="w-full min-h-screen pt-4 md:pt-16 relative z-10">
        <section className="px-6 md:px-20 w-full pb-32 md:pb-40">
          {/* Mobile Top Bar - HOME + Line */}
          <div className="md:hidden flex items-center mb-12 w-full gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-black text-sm font-medium tracking-widest uppercase"
            >
              HOME
            </button>
            <div className="h-[1px] bg-black w-24 opacity-20"></div>
          </div>

          <div className="flex flex-col md:flex-row gap-8 md:gap-24 w-full relative items-start">

            {/* Left Column: Text Content - Scrollable */}
            <div className="flex-1 text-left md:w-3/5 order-2 md:order-1">
              <motion.h1
                className="text-6xl sm:text-7xl md:text-[10rem] bonky-font text-black mb-6 md:mb-8 leading-none"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                About
              </motion.h1>

              <motion.h2
                className="text-2xl sm:text-3xl md:text-5xl bonky-font text-black mb-8 md:mb-10 leading-snug font-normal"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                I'm Aiman Rahimi. A Creative Developer translating design into digital feelings.
              </motion.h2>

              <motion.div
                className="space-y-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                <div className="space-y-6">
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl">
                    I believe code is more than just logic—it's a medium for craft.
                    Coming from a background in Occupational Safety & Health, I bring a
                    disciplined eye for detail into the messy, beautiful world of creative development.
                  </p>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl">
                    My work is about the "vibe"—ensuring that every interaction feels intuitive,
                    every transition is smooth, and every pixel serves a purpose. I don't just
                    build websites; I craft rhythmic digital spaces.
                  </p>
                </div>

                {/* Section blocks with modified minimalist dividers */}
                <div className="space-y-16 py-12">
                  <section className="relative pl-8">
                    <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-black/10" />
                    <h3 className="text-xs font-bold tracking-[0.3em] uppercase text-black/40 mb-6 font-sans">The Path</h3>
                    <div className="space-y-8">
                      <div>
                        <h4 className="text-xl bonky-font text-black mb-2">OSH Precision</h4>
                        <p className="text-gray-600 leading-relaxed max-w-xl font-sans">
                          A degree in safety taught me the importance of a solid foundation.
                          I apply that same systematic caution to my code, ensuring stability
                          without sacrificing the creative vision.
                        </p>
                      </div>
                      <div>
                        <h4 className="text-xl bonky-font text-black mb-2">Creative Dev & UI Craft</h4>
                        <p className="text-gray-600 leading-relaxed max-w-xl font-sans">
                          Merging technical capability with aesthetic intuition. I specialize
                          in the frontend—where code meets the human eye—focused on motion,
                          texture, and clean implementation.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section className="relative pl-8">
                    <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-black/10" />
                    <h3 className="text-xs font-bold tracking-[0.3em] uppercase text-black/40 mb-6 font-sans">Toolbox</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                      <div>
                        <h4 className="text-sm font-bold text-black uppercase mb-3 font-sans">The Visuals</h4>
                        <p className="text-gray-600 text-sm leading-relaxed font-sans">React / Next.js / Tailwind / Framer Motion / GSAP</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-black uppercase mb-3 font-sans">The Engine</h4>
                        <p className="text-gray-600 text-sm leading-relaxed font-sans">Node.js / Python / TypeScript / Supabase / PostgreSQL</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-black uppercase mb-3 font-sans">The Design</h4>
                        <p className="text-gray-600 text-sm leading-relaxed font-sans">Figma / Adobe Suite / Motion Design / Typography</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-black uppercase mb-3 font-sans">The Vibe</h4>
                        <p className="text-gray-600 text-sm leading-relaxed font-sans">Precision / Empathy / Narrative / Aesthetic Minimalism</p>
                      </div>
                    </div>
                  </section>

                  <section className="relative pl-8">
                    <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-black/10" />
                    <h3 className="text-xs font-bold tracking-[0.3em] uppercase text-black/40 mb-6 font-sans">Manifesto</h3>
                    <p className="text-xl bonky-font text-black leading-relaxed max-w-xl italic">
                      "Good code is invisible. Great code is a vibe—it's the silent heartbeat
                      of a beautiful digital experience."
                    </p>
                  </section>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Sticky Image Container - Stacks on mobile */}
            <div className="w-full md:w-1/3 md:sticky md:top-16 md:self-start h-fit mb-12 md:mb-16 order-1 md:order-2 flex justify-center md:overflow-visible relative">
              <motion.div
                className="w-full max-w-[340px] md:max-w-[420px] px-6"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: [0, -15, 0]
                }}
                transition={{
                  opacity: { duration: 0.6, delay: 0.3 },
                  scale: { duration: 0.6, delay: 0.3 },
                  y: {
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.9
                  }
                }}
              >
                <div className="relative group p-6">
                  {/* Concept 3: Modern Collage Elements */}

                  {/* 1. Vertical Background Typography */}
                  <div
                    className="absolute -left-6 md:-left-12 top-1/2 -translate-y-1/2 text-[10rem] md:text-[16rem] font-bold text-black/[0.02] select-none pointer-events-none bonky-font leading-none z-[-1]"
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg) translateY(50%)' }}
                  >
                    AIMAN
                  </div>

                  {/* 2. Parallax Floating Circle */}
                  <motion.div
                    className="absolute -top-3 -right-3 md:-top-6 md:-right-6 w-32 h-32 md:w-44 md:h-44 border border-black/[0.05] rounded-full z-[-1]"
                    whileHover={{
                      x: [0, 20, -15, 0],
                      y: [0, -25, 15, 0],
                      scale: 1.15
                    }}
                    transition={{
                      duration: 10,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />

                  {/* Main image container */}
                  <div className="relative aspect-[4/5] overflow-hidden shadow-2xl transition-all duration-1000 border border-black/5">
                    <img
                      src="/profile_photo.png"
                      alt="Aiman Rahimi"
                      className="w-full h-full object-cover transition-all duration-1000 ease-out group-hover:scale-105 object-[25%_center] mix-blend-multiply"
                    />
                    {/* Artistic gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-60 group-hover:opacity-0 transition-opacity duration-700" />
                  </div>

                  {/* Corner accent - subtle minimalist detail */}
                  <div className="absolute -bottom-2 -right-2 w-16 h-16 md:w-24 md:h-24 border-r border-b border-black/10 -z-10 transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1" />
                </div>
              </motion.div>
            </div>

          </div>
        </section>
      </main>
    </motion.div>
  );
};

export default About;
